import { useEffect, useMemo, useState } from "react";

import { supabase } from "@/integrations/supabase/client";

const REMOTE_URL_PATTERN = /^https?:\/\//i;
const SIGNED_URL_TTL_MS = 55 * 60 * 1000;
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();

export const isRemoteStorageUrl = (value: string | null | undefined) =>
  Boolean(value && REMOTE_URL_PATTERN.test(value));

function extractSupabaseStoragePath(bucket: string, value: string) {
  try {
    const pathname = new URL(value).pathname;
    const prefixes = [
      `/storage/v1/object/public/${bucket}/`,
      `/storage/v1/object/sign/${bucket}/`,
      `/storage/v1/object/authenticated/${bucket}/`,
    ];

    for (const prefix of prefixes) {
      const prefixIndex = pathname.indexOf(prefix);
      if (prefixIndex >= 0) {
        const nextPath = pathname.slice(prefixIndex + prefix.length);
        return nextPath || null;
      }
    }
  } catch {
    return null;
  }

  return null;
}

function normalizeStorageValue(bucket: string, value: string | null | undefined) {
  if (!value) return null;
  const normalized = value.trim();
  if (!normalized) return null;

  if (isRemoteStorageUrl(normalized)) {
    return extractSupabaseStoragePath(bucket, normalized) ?? normalized;
  }

  return normalized.split(/[?#]/)[0] ?? null;
}

export function canUseRemoteStorageUrlDirectly(bucket: string, value: string | null | undefined) {
  const normalizedValue = normalizeStorageValue(bucket, value);
  return Boolean(normalizedValue && isRemoteStorageUrl(normalizedValue));
}

function getCacheKey(bucket: string, value: string) {
  return `${bucket}::${value}`;
}

function getCachedSignedUrl(bucket: string, value: string) {
  const cached = signedUrlCache.get(getCacheKey(bucket, value));
  if (!cached) return null;

  if (cached.expiresAt <= Date.now()) {
    signedUrlCache.delete(getCacheKey(bucket, value));
    return null;
  }

  return cached.url;
}

function setCachedSignedUrl(bucket: string, value: string, url: string) {
  signedUrlCache.set(getCacheKey(bucket, value), {
    url,
    expiresAt: Date.now() + SIGNED_URL_TTL_MS,
  });
}

export const resolveSignedStorageUrls = async (bucket: string, values: Array<string | null | undefined>) => {
  const resolved = new Map<string, string>();
  const missingPaths = new Set<string>();
  const uniqueValues = Array.from(new Set(values.filter((value): value is string => Boolean(value))));

  for (const value of uniqueValues) {
    const rawValue = value.trim();
    const normalizedValue = normalizeStorageValue(bucket, rawValue);
    if (!normalizedValue) continue;

    if (isRemoteStorageUrl(normalizedValue)) {
      resolved.set(normalizedValue, normalizedValue);
      continue;
    }

    const cached = getCachedSignedUrl(bucket, rawValue) ?? getCachedSignedUrl(bucket, normalizedValue);
    if (cached) {
      resolved.set(normalizedValue, cached);
      continue;
    }

    missingPaths.add(normalizedValue);
  }

  if (missingPaths.size === 0) {
    return resolved;
  }

  const uniquePaths = Array.from(missingPaths);
  const { data, error } = await supabase.storage.from(bucket).createSignedUrls(uniquePaths, 60 * 60);

  if (error) {
    console.error(`Could not create signed URLs for ${bucket}:`, error);
    return resolved;
  }

  const signedUrlByPath = new Map<string, string>();
  uniquePaths.forEach((path, index) => {
    const signedUrl = data?.[index]?.signedUrl ?? "";
    if (!signedUrl) return;
    signedUrlByPath.set(path, signedUrl);
  });

  uniquePaths.forEach((normalizedValue) => {
    const signedUrl = signedUrlByPath.get(normalizedValue);
    if (!signedUrl) return;
    setCachedSignedUrl(bucket, normalizedValue, signedUrl);
    resolved.set(normalizedValue, signedUrl);
  });

  return resolved;
};

export const resolveStoredObjectUrl = (
  bucket: string,
  value: string | null | undefined,
  resolvedUrls: Map<string, string>,
) => {
  const normalizedValue = normalizeStorageValue(bucket, value);
  if (!normalizedValue) return null;
  if (isRemoteStorageUrl(normalizedValue)) return normalizedValue;
  return resolvedUrls.get(normalizedValue) ?? null;
};

export const resolveStoredObjectUrlAsync = async (bucket: string, value: string | null | undefined) => {
  const normalizedValue = normalizeStorageValue(bucket, value);
  if (!normalizedValue) return null;
  if (isRemoteStorageUrl(normalizedValue)) return normalizedValue;

  const cacheValue = value?.trim() ?? normalizedValue;
  const cached = getCachedSignedUrl(bucket, cacheValue) ?? getCachedSignedUrl(bucket, normalizedValue);
  if (cached) return cached;

  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(normalizedValue, 60 * 60);
  if (error || !data?.signedUrl) {
    if (error) {
      console.error(`Could not create signed URL for ${bucket}:`, error);
    }
    return null;
  }

  setCachedSignedUrl(bucket, cacheValue, data.signedUrl);
  setCachedSignedUrl(bucket, normalizedValue, data.signedUrl);
  return data.signedUrl;
};

export function useResolvedStorageUrl(bucket: string, value: string | null | undefined) {
  const [resolvedValue, setResolvedValue] = useState<string | null>(
    canUseRemoteStorageUrlDirectly(bucket, value) ? value?.trim() ?? null : null,
  );

  useEffect(() => {
    let active = true;

    if (!value) {
      setResolvedValue(null);
      return () => {
        active = false;
      };
    }

    if (canUseRemoteStorageUrlDirectly(bucket, value)) {
      setResolvedValue(value.trim());
      return () => {
        active = false;
      };
    }

    void resolveStoredObjectUrlAsync(bucket, value).then((nextValue) => {
      if (active) {
        setResolvedValue(nextValue);
      }
    });

    return () => {
      active = false;
    };
  }, [bucket, value]);

  return resolvedValue;
}

export function useResolvedStorageUrls(bucket: string, values: Array<string | null | undefined>) {
  const [resolvedUrls, setResolvedUrls] = useState<Map<string, string>>(new Map());
  const fingerprint = useMemo(() => values.join("||"), [values]);

  useEffect(() => {
    let active = true;

    void resolveSignedStorageUrls(bucket, values).then((nextMap) => {
      if (active) {
        setResolvedUrls(nextMap);
      }
    });

    return () => {
      active = false;
    };
  }, [bucket, fingerprint]);

  return values
    .map((value) => resolveStoredObjectUrl(bucket, value, resolvedUrls))
    .filter((value): value is string => Boolean(value));
}

export function useResolvedStorageUrlMap(bucket: string, values: Array<string | null | undefined>) {
  const [resolvedUrls, setResolvedUrls] = useState<Map<string, string>>(new Map());
  const fingerprint = useMemo(() => values.join("||"), [values]);

  useEffect(() => {
    let active = true;

    void resolveSignedStorageUrls(bucket, values).then((nextMap) => {
      if (active) {
        setResolvedUrls(nextMap);
      }
    });

    return () => {
      active = false;
    };
  }, [bucket, fingerprint]);

  return resolvedUrls;
}
